import { useNavigate } from "react-router-dom";
import { useCurrency } from "@/lib/currency";

/**
 * Pill-style variant selector for products sharing a `variant_group`.
 * Clicking a pill navigates to the sibling SKU (no full reload — React Router).
 * Props:
 *   variants: [{ id, name, price, variant_label, variant_order }]
 *   currentId: string
 */
export const VariantSelector = ({ variants = [], currentId }) => {
  const navigate = useNavigate();
  const { formatPrice } = useCurrency();
  if (!variants || variants.length < 2) return null;

  // 2 variants: side-by-side. 3+: dense 2-col / 3-col grid.
  const grid = variants.length === 2
    ? "grid-cols-2"
    : "grid-cols-2 sm:grid-cols-3";

  return (
    <div className="border border-neutral-200 bg-white p-4" data-testid="variant-selector">
      <div className="flex items-baseline justify-between mb-2.5">
        <span className="text-[10px] tracking-[0.3em] uppercase font-bold">Variant</span>
        <span className="text-[10px] tracking-[0.2em] uppercase text-neutral-500">
          {variants.length} options
        </span>
      </div>
      <div className={`grid ${grid} gap-2`}>
        {variants.map((v) => {
          const active = v.id === currentId;
          const labelSlug = (v.variant_label || "v").replace(/\s+/g, "-").replace(/[^a-zA-Z0-9-]/g, "");
          return (
            <button
              key={v.id}
              type="button"
              onClick={() => !active && navigate(`/product/${v.slug || v.id}`)}
              disabled={active}
              data-testid={`variant-${labelSlug}`}
              title={v.variant_label}
              className={`relative text-left px-3 py-2.5 border transition-all ${
                active
                  ? "border-black bg-black text-white cursor-default"
                  : "border-neutral-300 bg-white text-black hover:border-black hover:-translate-y-0.5"
              }`}
            >
              <div className={`text-[9px] tracking-[0.18em] uppercase ${active ? "text-neutral-300" : "text-neutral-500"}`}>
                {active ? "Selected" : "Switch"}
              </div>
              <div className="mt-0.5 flex items-baseline justify-between gap-2">
                <span className="font-heading text-sm font-black tracking-tight leading-tight line-clamp-2 break-words">
                  {v.variant_label}
                </span>
              </div>
              <div className={`mt-1 text-[11px] font-bold ${active ? "text-white" : "text-neutral-700"}`}>
                {formatPrice(v.price)}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};
